import {
  Box,
  Flex,
  OutlineCircleQuestionIcon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  Tooltip,
  useDisclosure,
} from '@frontend/legacy-components';

import { isMobileBrowser } from 'utils/browserUtils';

import ViewerButton from '../components/ViewerButton';

const controlTextProps = {
  fontSize: 'sm' as const,
  lineHeight: 'short' as const,
};

const ControlRow = ({ title, value }: { title: string; value: string }) => (
  <Flex alignItems="flex-start" gap={3} justify="space-between" w="100%">
    <Text flex="1" fontWeight="medium" minW={0} {...controlTextProps}>
      {title}
    </Text>
    <Text flex="1" minW={0} {...controlTextProps}>
      {value}
    </Text>
  </Flex>
);

interface ControlsProps {
  isMobile: boolean;
}

/**
 * 2D viewer controls (DXF/DWG files) — body only; popover title is shared with 3D.
 */
const Controls2DRows = ({ isMobile }: ControlsProps) => (
  <Flex direction="column" gap={0.5} minW="260px">
    <ControlRow title="Zoom:" value={isMobile ? 'Pinch' : 'Scroll'} />
    <ControlRow title="Pan:" value={isMobile ? 'Drag finger' : 'Drag RMB'} />
    <ControlRow title="Rotate:" value="Use rotate buttons" />
    <ControlRow
      title="Reset view:"
      value={isMobile ? 'Double tap' : 'Double-click or reset button'}
    />
  </Flex>
);

/**
 * 3D viewer controls (STEP, STL, etc.) — body only; popover title is shared with 2D.
 */
const Controls3DBody = ({ isMobile }: ControlsProps) => (
  <Flex direction="column" gap={0.5} minW="260px">
    <ControlRow
      title="Zoom:"
      value={isMobile ? 'Slide fingers apart' : 'Scroll'}
    />
    <ControlRow title="Rotate:" value={isMobile ? 'Drag finger' : 'Drag LMB'} />
    {!isMobile && <ControlRow title="Pan:" value="Drag RMB" />}
    <ControlRow
      title="Recenter trackball:"
      value={isMobile ? 'Double tap on body' : 'Double-click on body'}
    />

    <Text fontWeight="heavy" fontSize="md" lineHeight="short" mt={0.5} mb={0}>
      3D space actions
    </Text>

    <ControlRow
      title="Zoom to fit:"
      value={isMobile ? 'Long press on body' : 'Right-click on body'}
    />
    <ControlRow
      title="View normal to:"
      value={isMobile ? 'Long press on plane' : 'Right-click on plane'}
    />
    <ControlRow
      title="Create selection:"
      value={
        isMobile
          ? 'Long press on entity (when no selected entities)'
          : 'Right-click on entity (when no selected entities)'
      }
    />
    <ControlRow
      title="Zoom to selection:"
      value={
        isMobile
          ? 'Long press anywhere (when selected entities)'
          : 'Right-click anywhere (when selected entities)'
      }
    />
    <ControlRow
      title="Clear selection:"
      value={
        isMobile
          ? 'Long press anywhere (when selected entities)'
          : 'Right-click anywhere (when selected entities)'
      }
    />
    <ControlRow
      title="Focus:"
      value={isMobile ? 'Long press on body' : 'Right-click on body'}
    />
    <ControlRow
      title="Isolate body:"
      value={isMobile ? 'Long press on body' : 'Right-click on body'}
    />
    <ControlRow
      title="Hide body:"
      value={isMobile ? 'Long press on body' : 'Right-click on body'}
    />
    <ControlRow
      title="Show all bodies:"
      value={isMobile ? 'Long press anywhere' : 'Right-click anywhere'}
    />
  </Flex>
);

interface ControlsInfoProps {
  is2DMode?: boolean;
}

const VIEWER_CONTROLS_TITLE = 'Viewer controls';

const ControlsInfo = ({ is2DMode = false }: ControlsInfoProps) => {
  const isMobile = isMobileBrowser();
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      autoFocus
      placement="auto"
      size="sm"
    >
      <PopoverTrigger>
        <ViewerButton
          isActive={isOpen}
          aria-label={VIEWER_CONTROLS_TITLE}
          position="relative"
        >
          <Tooltip label={VIEWER_CONTROLS_TITLE} placement="bottom">
            <Box
              position="absolute"
              inset={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <OutlineCircleQuestionIcon boxSize={3} />
            </Box>
          </Tooltip>
        </ViewerButton>
      </PopoverTrigger>
      <PopoverContent>
        <PopoverHeader textTransform="none">
          {VIEWER_CONTROLS_TITLE}
        </PopoverHeader>
        <PopoverBody>
          {is2DMode ? (
            <Controls2DRows isMobile={isMobile} />
          ) : (
            <Controls3DBody isMobile={isMobile} />
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default ControlsInfo;
